import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  AlertTriangle,
  ArrowDownRight,
  ArrowRight,
  ArrowUpRight,
  CheckCircle2,
  Lightbulb,
  TrendingDown,
  TrendingUp,
  Wallet,
} from 'lucide-react';
import { supabase } from '../../lib/supabaseClient';
import {
  ForecastCategory,
  MonthForecast,
  normalizeMonthForecast,
} from '../../lib/forecastTypes';
import { useImpersonation } from '../../contexts/ImpersonationContext';
import { AnimatedCard } from '../animations/AnimatedCard';
import { AnimatedNumber } from '../animations/AnimatedNumber';
import { AnimatedProgressBar } from '../animations/AnimatedProgressBar';

const formatEuro = (n: number) =>
  new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 0,
  }).format(n);

type LoadState = 'loading' | 'ready' | 'unavailable' | 'error';
type ForecastStatus = 'on_track' | 'watch' | 'over' | 'no_budget';

function deriveStatus(
  data: MonthForecast,
  budgetMensuel: number | null,
): ForecastStatus {
  if (budgetMensuel == null || budgetMensuel <= 0) {
    if (data.categories.some((c) => c.statut === 'at_risk')) return 'over';
    if (data.categories.some((c) => c.statut === 'warning')) return 'watch';
    return data.solde_projete >= 0 ? 'on_track' : 'watch';
  }
  const ratio = data.projection / budgetMensuel;
  if (ratio > 1) return 'over';
  if (ratio > 0.9) return 'watch';
  return 'on_track';
}

const STATUS_UI: Record<
  ForecastStatus,
  { label: string; className: string; Icon: typeof CheckCircle2 }
> = {
  on_track: {
    label: 'En bonne voie',
    className: 'bg-emerald-500/15 text-emerald-300 ring-1 ring-emerald-500/30',
    Icon: CheckCircle2,
  },
  watch: {
    label: 'À surveiller',
    className: 'bg-amber-500/15 text-amber-300 ring-1 ring-amber-500/30',
    Icon: AlertTriangle,
  },
  over: {
    label: 'Dépassement prévu',
    className: 'bg-red-500/15 text-red-300 ring-1 ring-red-500/35',
    Icon: TrendingDown,
  },
  no_budget: {
    label: 'Sans budget',
    className: 'bg-white/5 text-dark-300 ring-1 ring-white/10',
    Icon: Lightbulb,
  },
};

const CAT_COLORS = [
  { bar: 'bg-amber-400', soft: 'bg-amber-500/15', text: 'text-amber-300', iconBg: 'bg-amber-500/20' },
  { bar: 'bg-violet-400', soft: 'bg-violet-500/15', text: 'text-violet-300', iconBg: 'bg-violet-500/20' },
  { bar: 'bg-orange-400', soft: 'bg-orange-500/15', text: 'text-orange-300', iconBg: 'bg-orange-500/20' },
  { bar: 'bg-sky-400', soft: 'bg-sky-500/15', text: 'text-sky-300', iconBg: 'bg-sky-500/20' },
];

/** Mini sparkline décoratif (pas de série journalière côté RPC). */
function MiniSpark({
  tone,
}: {
  tone: 'red' | 'violet' | 'green';
}) {
  const stroke =
    tone === 'red' ? '#f87171' : tone === 'violet' ? '#a78bfa' : '#34d399';
  const fill =
    tone === 'red'
      ? 'url(#sparkRed)'
      : tone === 'violet'
        ? 'url(#sparkViolet)'
        : 'url(#sparkGreen)';
  const d =
    tone === 'red'
      ? 'M0,28 L12,22 L24,24 L36,14 L48,18 L60,8 L72,12 L84,4 L96,10 L112,2'
      : tone === 'violet'
        ? 'M0,20 L14,18 L28,22 L42,16 L56,19 L70,12 L84,15 L98,10 L112,14'
        : 'M0,24 L14,20 L28,16 L42,18 L56,12 L70,14 L84,8 L98,10 L112,6';

  return (
    <svg
      className="pointer-events-none absolute inset-x-0 bottom-0 h-14 w-full opacity-40"
      viewBox="0 0 112 32"
      preserveAspectRatio="none"
      aria-hidden
    >
      <defs>
        <linearGradient id="sparkRed" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#f87171" stopOpacity="0.45" />
          <stop offset="100%" stopColor="#f87171" stopOpacity="0" />
        </linearGradient>
        <linearGradient id="sparkViolet" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#a78bfa" stopOpacity="0.4" />
          <stop offset="100%" stopColor="#a78bfa" stopOpacity="0" />
        </linearGradient>
        <linearGradient id="sparkGreen" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#34d399" stopOpacity="0.4" />
          <stop offset="100%" stopColor="#34d399" stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={`${d} L112,32 L0,32 Z`} fill={fill} />
      <path d={d} fill="none" stroke={stroke} strokeWidth="1.6" />
    </svg>
  );
}

const ForecastWidget: React.FC = () => {
  const { impersonatedUserId } = useImpersonation();
  const [state, setState] = useState<LoadState>('loading');
  const [data, setData] = useState<MonthForecast | null>(null);

  const load = useCallback(async () => {
    setState('loading');
    try {
      const { data: raw, error } = await supabase.rpc('get_month_forecast', {
        ...(impersonatedUserId ? { p_user_id: impersonatedUserId } : {}),
      });
      if (error) {
        const msg = (error.message || '').toLowerCase();
        const code = String((error as { code?: string }).code || '');
        if (
          code === 'PGRST202' ||
          code === '42883' ||
          msg.includes('could not find') ||
          msg.includes('does not exist') ||
          msg.includes('get_month_forecast')
        ) {
          setState('unavailable');
          setData(null);
          return;
        }
        console.warn('[ForecastWidget]', error);
        setState('error');
        setData(null);
        return;
      }
      const normalized = normalizeMonthForecast(raw);
      if (!normalized) {
        setState('error');
        return;
      }
      setData(normalized);
      setState('ready');
    } catch (e) {
      console.warn('[ForecastWidget] exception:', e);
      setState('error');
    }
  }, [impersonatedUserId]);

  useEffect(() => {
    load();
  }, [load]);

  const derived = useMemo(() => {
    if (!data) return null;
    const budgets = data.categories
      .map((c) => c.budget_limite)
      .filter((b): b is number => b != null && b > 0);
    const budgetMensuel = budgets.length > 0 ? budgets.reduce((a, b) => a + b, 0) : null;
    const resteEstime = Math.max(data.projection - data.depense_mois, 0);
    const status =
      budgetMensuel == null ? ('no_budget' as const) : deriveStatus(data, budgetMensuel);
    const ecart =
      budgetMensuel != null ? Math.round((budgetMensuel - data.projection) * 100) / 100 : null;
    const ecartPct =
      budgetMensuel != null && budgetMensuel > 0
        ? Math.round(((budgetMensuel - data.projection) / budgetMensuel) * 1000) / 10
        : null;
    const pctOf = (v: number) =>
      budgetMensuel && budgetMensuel > 0
        ? Math.round((v / budgetMensuel) * 1000) / 10
        : null;
    const progressPct =
      data.jours_du_mois > 0
        ? Math.min(100, Math.round((data.jour_actuel / data.jours_du_mois) * 100))
        : 0;
    const riskCats = data.categories
      .filter((c) => c.statut === 'at_risk' || c.statut === 'warning')
      .sort((a, b) => {
        const ea = (a.budget_limite ?? 0) - a.projection_fin_mois;
        const eb = (b.budget_limite ?? 0) - b.projection_fin_mois;
        return ea - eb;
      })
      .slice(0, 4);
    return {
      budgetMensuel,
      resteEstime,
      status,
      ecart,
      ecartPct,
      depensePct: pctOf(data.depense_mois),
      restePct: pctOf(resteEstime),
      previsPct: pctOf(data.projection),
      progressPct,
      riskCats,
      soldePositif: data.solde_projete >= 0,
      resteDispo: ecart,
    };
  }, [data]);

  if (state === 'unavailable') {
    return (
      <div className="card-premium animate-fade-up border border-dashed border-white/10">
        <p className="relative z-[1] text-body-sm text-dark-500">Prévision indisponible</p>
      </div>
    );
  }

  if (state === 'loading') {
    return (
      <div className="card-premium animate-fade-up">
        <div className="relative z-[1] space-y-4">
          <div className="h-5 w-56 animate-pulse rounded bg-dark-700" />
          <div className="h-2 w-full animate-pulse rounded-full bg-dark-700" />
          <div className="grid grid-cols-3 gap-3">
            <div className="h-24 animate-pulse rounded-2xl bg-dark-700/70" />
            <div className="h-24 animate-pulse rounded-2xl bg-dark-700/70" />
            <div className="h-24 animate-pulse rounded-2xl bg-dark-700/70" />
          </div>
          <div className="h-40 animate-pulse rounded-2xl bg-dark-700/50" />
        </div>
      </div>
    );
  }

  if (state === 'error' || !data || !derived) {
    return (
      <div className="card-premium animate-fade-up">
        <p className="relative z-[1] text-body-sm text-dark-400">
          Impossible de charger la prévision.
          <button
            type="button"
            onClick={load}
            className="ml-2 font-semibold text-primary-400 hover:text-primary-300"
          >
            Réessayer
          </button>
        </p>
      </div>
    );
  }

  const statusUi = STATUS_UI[derived.status];
  const StatusIcon = statusUi.Icon;
  const budget = derived.budgetMensuel ?? 0;
  const scaleMax = Math.max(budget, data.projection, 1);
  const budgetBarPct = Math.min(100, (budget / scaleMax) * 100);
  const forecastBarPct = Math.min(100, (data.projection / scaleMax) * 100);

  return (
    <AnimatedCard className="card-premium overflow-hidden" delay={0.2}>
      <div className="relative z-[1] space-y-5">
        {/* Header */}
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0 space-y-2">
            <div className="flex flex-wrap items-center gap-2.5">
              <div className="flex items-center gap-2">
                <TrendingUp className="h-5 w-5 text-primary-400" />
                <h3 className="text-lg font-bold text-white">Prévision fin de mois</h3>
              </div>
              <span
                className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold ${statusUi.className}`}
              >
                <StatusIcon className="h-3 w-3" />
                {statusUi.label}
              </span>
            </div>
            <p className="text-sm text-dark-400">
              Il reste {data.jours_restants} jour{data.jours_restants > 1 ? 's' : ''} dans ce
              mois
            </p>
          </div>
          <Link
            to="/budgets"
            className="inline-flex shrink-0 items-center rounded-xl border border-white/10 bg-white/[0.03] px-3.5 py-2 text-xs font-semibold text-dark-200 transition hover:border-primary-500/30 hover:text-white"
          >
            Gérer mes budgets
          </Link>
        </div>

        {/* Month progress */}
        <div>
          <div className="mb-1.5 flex justify-between text-[11px] text-dark-500">
            <span>Avancement du mois</span>
            <span className="font-semibold text-dark-300">{derived.progressPct} %</span>
          </div>
          <div className="h-2 overflow-hidden rounded-full bg-dark-800">
            <AnimatedProgressBar
              percentage={derived.progressPct}
              className="h-full rounded-full bg-gradient-to-r from-primary-600 to-primary-400"
            />
          </div>
        </div>

        {/* KPI tiles */}
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <KpiCard
            label="Dépensé"
            value={formatEuro(data.depense_mois)}
            numericValue={data.depense_mois}
            hint={
              derived.depensePct != null
                ? `${derived.depensePct} % du budget`
                : 'Ce mois'
            }
            tone="red"
            icon={<ArrowDownRight className="h-4 w-4" />}
            delay={0}
          />
          <KpiCard
            label="Reste estimé"
            value={formatEuro(derived.resteEstime)}
            numericValue={derived.resteEstime}
            hint={
              derived.restePct != null
                ? `${derived.restePct} % du budget`
                : 'Jusqu’à fin de mois'
            }
            tone="violet"
            icon={<Wallet className="h-4 w-4" />}
            delay={0.05}
          />
          <KpiCard
            label="Prévision totale"
            value={formatEuro(data.projection)}
            numericValue={data.projection}
            hint={
              derived.previsPct != null
                ? `${derived.previsPct} % du budget`
                : 'Projection fin de mois'
            }
            tone="green"
            icon={<ArrowUpRight className="h-4 w-4" />}
            delay={0.1}
          />
        </div>

        {/* Secondary metrics */}
        <div className="grid grid-cols-2 gap-3 rounded-2xl border border-white/[0.06] bg-dark-950/40 px-4 py-3 lg:grid-cols-4">
          <MiniStat
            label="Budget mensuel"
            value={
              derived.budgetMensuel != null
                ? formatEuro(derived.budgetMensuel)
                : '—'
            }
          />
          <MiniStat label="Revenu du mois" value={formatEuro(data.revenu_mois)} />
          <MiniStat
            label="Solde projeté"
            value={`${derived.soldePositif ? '+' : ''}${formatEuro(data.solde_projete)}`}
            valueClass={derived.soldePositif ? 'text-emerald-400' : 'text-red-400'}
          />
          <MiniStat
            label="Reste disponible"
            value={
              derived.resteDispo == null
                ? '—'
                : `${derived.resteDispo >= 0 ? '' : '−'}${formatEuro(Math.abs(derived.resteDispo))}`
            }
            valueClass={
              derived.resteDispo == null
                ? undefined
                : derived.resteDispo >= 0
                  ? 'text-emerald-400'
                  : 'text-red-400'
            }
            warn={derived.resteDispo != null && derived.resteDispo < 0}
          />
        </div>

        {/* Detail 2 cols */}
        <div className="grid gap-4 lg:grid-cols-2">
          {/* Écart vs budget */}
          <div className="rounded-2xl border border-white/[0.06] bg-dark-950/30 p-4">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-dark-500">
              Écart vs budget
            </p>
            {derived.ecart != null && derived.ecartPct != null ? (
              <>
                <p
                  className={`mt-1 text-3xl font-bold tracking-tight ${
                    derived.ecart >= 0 ? 'text-emerald-400' : 'text-red-400'
                  }`}
                >
                  {derived.ecart >= 0 ? '+' : '−'}
                  {formatEuro(Math.abs(derived.ecart))}
                  <span className="ml-2 text-sm font-medium opacity-70">
                    ({Math.abs(derived.ecartPct)} %{' '}
                    {derived.ecart >= 0 ? 'sous' : 'au-dessus'})
                  </span>
                </p>

                <div className="mt-4 space-y-2">
                  <div className="relative h-3 overflow-hidden rounded-full bg-dark-800">
                    <div
                      className="absolute inset-y-0 left-0 rounded-full bg-gradient-to-r from-emerald-500 via-amber-400 to-red-500 opacity-90"
                      style={{ width: `${forecastBarPct}%` }}
                    />
                    {budget > 0 && (
                      <div
                        className="absolute top-0 z-10 h-full w-0.5 bg-white shadow-[0_0_6px_rgba(255,255,255,0.8)]"
                        style={{ left: `calc(${budgetBarPct}% - 1px)` }}
                        title={`Budget ${formatEuro(budget)}`}
                      />
                    )}
                  </div>
                  <div className="flex justify-between text-[11px] text-dark-500">
                    <span>Budget {formatEuro(budget)}</span>
                    <span>Prévision {formatEuro(data.projection)}</span>
                  </div>
                </div>

                <div className="mt-4 flex items-start gap-2 rounded-xl bg-white/[0.03] px-3 py-2.5 text-sm text-dark-300">
                  <Lightbulb className="mt-0.5 h-4 w-4 shrink-0 text-amber-300" />
                  <span>
                    {derived.status === 'over'
                      ? 'Astuce : réduisez les dépenses variables sur la fin du mois pour limiter le dépassement.'
                      : derived.status === 'watch'
                        ? 'Astuce : vous approchez du plafond — surveillez les catégories à risque.'
                        : 'Astuce : vous êtes sous budget — gardez le rythme jusqu’à la fin du mois.'}
                  </span>
                </div>
              </>
            ) : (
              <div className="mt-3 flex items-start gap-2 text-sm text-dark-400">
                <Lightbulb className="mt-0.5 h-4 w-4 shrink-0 text-primary-400" />
                <span>
                  Ajoutez des budgets par catégorie pour voir l’écart.{' '}
                  <Link to="/budgets" className="font-semibold text-primary-400">
                    Configurer →
                  </Link>
                </span>
              </div>
            )}
          </div>

          {/* Catégories à risque */}
          <div className="rounded-2xl border border-white/[0.06] bg-dark-950/30 p-4">
            <p className="mb-3 text-[11px] font-semibold uppercase tracking-wide text-dark-500">
              Catégories à risque
            </p>
            {derived.riskCats.length === 0 ? (
              <div className="flex items-start gap-2 rounded-xl bg-emerald-500/10 px-3 py-2.5 text-sm text-emerald-300">
                <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" />
                <span>Aucune catégorie à risque pour le moment</span>
              </div>
            ) : (
              <ul className="space-y-3">
                {derived.riskCats.map((c, i) => (
                  <RiskCategoryRow key={c.categorie} cat={c} color={CAT_COLORS[i % CAT_COLORS.length]} />
                ))}
              </ul>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex flex-col gap-3 border-t border-white/[0.06] pt-4 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-sm text-dark-400">
            <span className="font-semibold text-dark-300">Conseil du moment — </span>
            {derived.status === 'over'
              ? 'La trajectoire dépasse le budget alloué. Priorisez les postes à risque ci-dessus.'
              : derived.status === 'watch'
                ? 'Vous êtes proches du plafond : un petit ajustement suffit souvent.'
                : derived.budgetMensuel == null
                  ? 'Configurez vos budgets pour affiner la prévision et les alertes.'
                  : 'Vous êtes sur une bonne trajectoire par rapport à votre budget.'}
          </p>
          <Link
            to="/charts"
            className="inline-flex shrink-0 items-center justify-center gap-2 rounded-xl bg-primary-500 px-4 py-2.5 text-sm font-bold text-dark-950 shadow-glow-green transition hover:bg-primary-400"
          >
            Voir l&apos;analyse détaillée
            <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </div>
    </AnimatedCard>
  );
};

function KpiCard({
  label,
  value,
  numericValue,
  hint,
  tone,
  icon,
  delay = 0,
}: {
  label: string;
  value: string;
  numericValue?: number;
  hint: string;
  tone: 'red' | 'violet' | 'green';
  icon: React.ReactNode;
  delay?: number;
}) {
  const styles = {
    red: {
      wrap: 'from-red-500/10 to-transparent ring-red-500/15',
      icon: 'bg-red-500/20 text-red-300',
      hint: 'text-red-300/80',
    },
    violet: {
      wrap: 'from-violet-500/10 to-transparent ring-violet-500/15',
      icon: 'bg-violet-500/20 text-violet-300',
      hint: 'text-violet-300/80',
    },
    green: {
      wrap: 'from-emerald-500/10 to-transparent ring-emerald-500/15',
      icon: 'bg-emerald-500/20 text-emerald-300',
      hint: 'text-emerald-300/80',
    },
  }[tone];

  return (
    <AnimatedCard
      className={`relative overflow-hidden rounded-2xl bg-gradient-to-br ${styles.wrap} ring-1 px-4 pb-10 pt-3.5`}
      delay={delay}
    >
      <div className="relative z-[1] flex items-start justify-between gap-2">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-wide text-dark-500">
            {label}
          </p>
          <p className="mt-1 text-2xl font-bold tracking-tight text-white" title={value}>
            {typeof numericValue === 'number' ? (
              <AnimatedNumber value={numericValue} suffix=" €" decimals={0} />
            ) : (
              value
            )}
          </p>
          <p className={`mt-1 text-[11px] font-medium ${styles.hint}`}>{hint}</p>
        </div>
        <div className={`rounded-full p-2 ${styles.icon}`}>{icon}</div>
      </div>
      <MiniSpark tone={tone} />
    </AnimatedCard>
  );
}

function MiniStat({
  label,
  value,
  valueClass,
  warn,
}: {
  label: string;
  value: string;
  valueClass?: string;
  warn?: boolean;
}) {
  return (
    <div>
      <p className="text-[10px] font-medium uppercase tracking-wide text-dark-500">{label}</p>
      <p className={`mt-0.5 flex items-center gap-1 text-sm font-bold text-white ${valueClass || ''}`}>
        {warn && <AlertTriangle className="h-3.5 w-3.5 text-red-400" />}
        {value}
      </p>
    </div>
  );
}

function RiskCategoryRow({
  cat,
  color,
}: {
  cat: ForecastCategory;
  color: (typeof CAT_COLORS)[number];
}) {
  const budget = cat.budget_limite ?? 0;
  const ecart = budget > 0 ? budget - cat.projection_fin_mois : null;
  const usage =
    budget > 0
      ? Math.min(100, Math.round((cat.projection_fin_mois / budget) * 100))
      : 0;

  return (
    <li className="flex items-start gap-3">
      <div className={`mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-xl ${color.iconBg}`}>
        <span className={`text-xs font-bold ${color.text}`}>
          {cat.categorie.slice(0, 1).toUpperCase()}
        </span>
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline justify-between gap-2">
          <p className="truncate text-sm font-semibold text-white">{cat.categorie}</p>
          {ecart != null && (
            <p
              className={`shrink-0 text-sm font-bold ${
                ecart >= 0 ? 'text-emerald-400' : 'text-red-400'
              }`}
            >
              {ecart >= 0 ? '+' : '−'}
              {formatEuro(Math.abs(ecart))}
            </p>
          )}
        </div>
        <p className="text-[11px] text-dark-500">
          Prévision {formatEuro(cat.projection_fin_mois)}
          {budget > 0 ? ` / ${formatEuro(budget)}` : ''}
        </p>
        <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-dark-800">
          <div
            className={`h-full rounded-full ${color.bar}`}
            style={{ width: `${usage}%` }}
          />
        </div>
      </div>
    </li>
  );
}

export default ForecastWidget;
